import type { ReactElement } from 'react';
import styled from 'styled-components';

interface Props {
  /** Client organization; scopes the Zendesk search link. */
  organization: string;
  onNew: () => void;
}

// Zendesk agent search; we append an org-scoped query for the current event.
const ZENDESK_SEARCH_BASE = 'https://livecontrol.zendesk.com/agent/search/1?q=';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 13px;
  min-height: 34px;
`;

const Title = styled.div`
  color: var(--white);
  font-weight: 700;
  font-size: 15px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const IconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(176, 186, 209, 0.8);
  text-decoration: none;
  transition: all var(--transition);

  i {
    font-size: 12px;
  }

  &:hover,
  &:focus {
    outline: none;
    color: var(--white);
    border-color: rgba(255, 255, 255, 0.22);
    text-decoration: none;
  }
`;

const NewButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  border-radius: 8px;
  padding: 9px 13px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: opacity 0.15s;

  i {
    font-size: 14px;
  }

  &:hover,
  &:focus {
    outline: none;
    opacity: 0.92;
  }
`;

// Title row of the Comms tab: open-in-Zendesk plus the New Ticket button.
export const TicketsHeader = ({ organization, onNew }: Props): ReactElement => {
  const query = encodeURIComponent(`type:organization "${organization}"`);
  const zendeskUrl = `${ZENDESK_SEARCH_BASE}${query}`;

  return (
    <Header>
      <Title>Zendesk Tickets</Title>

      <Actions>
        <IconLink href={zendeskUrl} target='_blank' rel='noreferrer' title='Open in Zendesk'>
          <i className='far fa-external-link' />
        </IconLink>

        <NewButton type='button' className='cc-primary' onClick={onNew}>
          <i className='far fa-plus' />
          New Ticket
        </NewButton>
      </Actions>
    </Header>
  );
};
